import type { IntegrationTestResult, SettingsHistoryEntry } from "../domain/settings/system-settings.js";

export interface StoredSettingsRecord {
  readonly payload: unknown;
  readonly version: number;
  readonly updatedAt: string | null;
  readonly updatedBy: string | null;
}

export interface SystemSettingsRepository {
  load(): Promise<StoredSettingsRecord | null>;
  /**
   * Grava SOMENTE se a versão gravada ainda for `expectedVersion` (0 = ainda
   * não existe registro). Devolve `false` quando outra gravação chegou antes —
   * quem salvou por último não apaga em silêncio o que o outro gravou.
   */
  saveIfVersion(payload: unknown, expectedVersion: number, meta: { updatedAt: string; updatedBy: string }): Promise<boolean>;
  appendHistory(entries: readonly SettingsHistoryEntry[]): Promise<void>;
  listHistory(limit?: number): Promise<SettingsHistoryEntry[]>;
  /**
   * Próximo número do escopo, por INCREMENTO ATÔMICO. Na primeira chamada o
   * contador nasce em `seed` (quantos documentos o escopo já tinha antes do
   * contador existir) e devolve `seed + 1`.
   */
  nextSequence(scopeKey: string, seed: number): Promise<number>;
  /**
   * Último teste de conexão por integração — registro OPERACIONAL, guardado
   * fora do registro versionado: testar não pode fazer a gravação de quem
   * está editando a configuração ser recusada por conflito de versão.
   */
  recordIntegrationTest(integrationId: string, result: IntegrationTestResult): Promise<void>;
  listIntegrationTests(): Promise<Record<string, IntegrationTestResult>>;
  /** Grava, lê e apaga um registro de teste — diagnóstico do armazenamento. */
  probe(): Promise<{ ok: boolean; message: string }>;
}

/**
 * Cofre dos segredos das integrações (client secret, PIN, token). Não tem
 * leitura para fora: o serviço só grava, confere se existe e remove. O valor
 * aberto nunca sai daqui para a tela, o histórico ou o log.
 */
export interface SettingsSecretStore {
  readonly available: boolean;
  /** Por que não há cofre (ex.: modo navegador, chave do servidor ausente). */
  readonly unavailableReason: string;
  put(name: string, value: string): Promise<void>;
  has(name: string): Promise<boolean>;
  remove(name: string): Promise<void>;
}

/** Sem servidor não há onde guardar segredo com segurança — e ele não é guardado. */
export class UnavailableSecretStore implements SettingsSecretStore {
  readonly available = false;
  constructor(readonly unavailableReason: string) {}
  async put(): Promise<void> { throw new Error(this.unavailableReason); }
  async has(): Promise<boolean> { return false; }
  async remove(): Promise<void> { /* nada guardado */ }
}
